import { createLogger } from '@svenskaspelab/nova-core/log';

const logger = createLogger('nova-account-tur:day-time-limit');
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { getDayTimeLimitData } from '../../../../../lib/message-producers/day-time-limit/cache-server-actions.js';

type InitProps = {
  showSoonEndMessage: (minutes: number) => void;
  showEndMessage: () => void;
};

export default function useDayTimeLimit() {
  const soonMsgTimeoutID = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const endMsgTimeoutID = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { isFetched, data: dayTimeLimitModel } = useQuery({
    queryKey: ['dayTimeLimit'],
    queryFn: () => getDayTimeLimitData(),
  });

  useEffect(() => {
    // Clear timeouts on unmount to prevent memory leaks and unwanted behavior
    return () => {
      clearTimeout(soonMsgTimeoutID.current);
      clearTimeout(endMsgTimeoutID.current);
    };
  }, []);

  // Use for dev
  // const timeToSoonEnd = 2000
  // const timeForSoonMessage = timeToSoonEnd ? Math.floor(timeToSoonEnd / (60 * 1000)) : 5; // Calculate time for soon message in minutes based on timeToSoonEnd
  // console.log('Calculated timeForSoonMessage:', timeForSoonMessage);

  /**
   * Init function, setting up timers for day time limit messages if customer has day time limit set.
   *
   * @param root0 - Object containing functions for showing soon end and end messages.
   * @param root0.showSoonEndMessage - Function to show the soon end message.
   * @param root0.showEndMessage - Function to show the end message.
   */
  const init = useCallback(
    ({ showSoonEndMessage, showEndMessage }: InitProps) => {
      if (!dayTimeLimitModel?.dayTimeLimitIsSet) {
        logger.info('Customer without day time limit set, timers not started');
        return;
      }
      const { timeToEnd, timeToSoonEnd, timeForSoonMessage } = dayTimeLimitModel;

      // TODO: Set up timers for dialogs after midnight. Probably it will be done in the same way as below. We calculates time for
      //  that serversida and add it ass separate properties at the data object. (Legacy comment)

      if (timeToSoonEnd) {
        const time = Math.max(timeToSoonEnd, 0);
        clearTimeout(soonMsgTimeoutID.current); // Clear previous timeout to prevent duplicates
        logger.debug(
          'Time to soon end:',
          Math.floor(timeToSoonEnd / 1000 / 60),
          'minutes',
          Math.floor((timeToSoonEnd / 1000) % 60),
          'seconds'
        );
        const id = setTimeout(() => {
          showSoonEndMessage(timeForSoonMessage);
        }, time);
        soonMsgTimeoutID.current = id;
      }

      if (timeToEnd) {
        clearTimeout(endMsgTimeoutID.current); // Clear previous timeout to prevent duplicates
        logger.debug(
          'Time to end:',
          Math.floor(timeToEnd / 1000 / 60),
          'minutes',
          Math.floor((timeToEnd / 1000) % 60),
          'seconds'
        );
        const id = setTimeout(() => {
          showEndMessage();
        }, timeToEnd);
        endMsgTimeoutID.current = id;
      }
    },
    [dayTimeLimitModel]
  );

  return { init, isFetched };
}
